import { createMiaApi, MiaWidgetController } from './MiaWidget';
import type { MiaApi, MiaInitOptions } from './types';
import { debugLog } from './debug';
import { readScriptDataAttrs } from './utils';
import { MIA_WIDGET_VERSION } from './version';

declare global {
  interface Window {
    MIA: MiaApi;
    __MIA_SDK_LOADED__?: string;
    __MIA_AUTO_INIT_DONE__?: boolean;
  }
}

function createSdk(): MiaApi {
  const controller = new MiaWidgetController();
  return createMiaApi(controller);
}

function shouldDebugFromPage(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('mia_debug') === '1') {
      return true;
    }
  } catch {
    // ignore
  }
  return Array.from(document.getElementsByTagName('script')).some(
    (s) => s.getAttribute('data-debug') === 'true',
  );
}

function resolveApi(): MiaApi {
  if (typeof window === 'undefined') {
    return createSdk();
  }

  if (window.__MIA_SDK_LOADED__ && window.MIA && typeof window.MIA.init === 'function') {
    if (shouldDebugFromPage()) {
      debugLog.enable(true);
      debugLog.warn('sdk:duplicate-script-ignored', {
        version: window.MIA.version ?? MIA_WIDGET_VERSION,
        loadedMarker: window.__MIA_SDK_LOADED__,
      });
    }
    return window.MIA;
  }

  const api = createSdk();
  window.MIA = api;
  window.__MIA_SDK_LOADED__ = MIA_WIDGET_VERSION;
  return api;
}

const api = resolveApi();

function autoInit(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (window.__MIA_AUTO_INIT_DONE__) return;

  const attrs = readScriptDataAttrs(document);
  // Programmatic hosts: omit data-widget-key, or set data-auto-init="false".
  if (!attrs.widgetKey || attrs.autoInit === false) return;

  window.__MIA_AUTO_INIT_DONE__ = true;

  const options: MiaInitOptions = {
    widgetKey: attrs.widgetKey,
    apiBaseUrl: attrs.apiBaseUrl,
    position: attrs.position,
    locale: attrs.locale,
  };

  void api.init(options).catch(() => {
    // errors via event + optional fallback UI
  });
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit, { once: true });
  } else {
    autoInit();
  }
}

export default api;
export type { MiaApi, MiaInitOptions };
